import type { SpecPushPreview, SpecPushPreviewTab } from "@/hooks/spec-push-preview";
import type { SpecLinesGroup, SpecLinesLine, SpecLinesSelection } from "@/hooks/specifications";
import {
  GROUP_FIELD_LABEL,
  lineFieldLabel,
  notesText,
  NOT_PAID_UPGRADE_TEXT,
  NOT_PER_PLAN_TEXT,
  selectionFieldLabel,
} from "@/lib/spec-base-changes";
import { PUSH_KIND_LABEL } from "@/lib/spec-push-copy";
import { type LineLocationType, locationsText } from "@/lib/spec-lines-display";

// The push preview's model (#1090; #1049's brief, R1–R9): the server's answer
// mapped onto what the details page's `SpecGroup` and `SpecLineRows` already
// take, plus the facts the page has no field for — and every word the dialog
// says that is built from data: the type filter and its counts, the Draft
// tabs' labels, the info icon's push sentences, the announcement, the legend,
// the version line, the Draft note and the not-applied rows.
//
// Every value is the server's (ADR-0022 decision 12): which rows the push
// touches, what each becomes and what it was are worked out there by the
// push's own merge. This module picks and words them, and never matches rows.

export type PushKind = "added" | "changed" | "moved" | "removed";
export type PushFilter = "all" | PushKind;

export const PUSH_KINDS: readonly PushKind[] = ["added", "changed", "moved", "removed"];

/** The preview's two wordings: before a push, and the read-only history's after one (Q15). */
export type PreviewTense = "preview" | "history";

/** One list's counts, as the server sends them. */
export interface PushCounts {
  added: number;
  changed: number;
  moved: number;
  removed: number;
  total: number;
  notApplied: number;
}

/** "Coastal V8": the pushed version as the sentences name it. */
export function versionName(base: string, code: string | null): string {
  const trimmed = code?.trim();
  return trimmed ? `${base} V${trimmed}` : base;
}

const codeLabel = (code: string | null) =>
  code?.trim() ? `V${code.trim()}` : "the uncoded version";

/**
 * The line under the title: "Coastal V7 → V8", and "· this community has a
 * Draft open" when it has one (the brief).
 */
export function versionLine(preview: {
  baseSpecificationName: string;
  parentVersionCode: string | null;
  pushedVersionCode: string | null;
  draftOpen: boolean;
}): string {
  const line = `${preview.baseSpecificationName} ${codeLabel(preview.parentVersionCode)} → ${codeLabel(preview.pushedVersionCode)}`;
  return preview.draftOpen ? `${line} · this community has a Draft open` : line;
}

/** The legend's three parts: the lead, the struck old side, the new side. */
export function legend(
  community: string,
  preview: { parentVersionCode: string | null; pushedVersionCode: string | null },
  tense: PreviewTense,
): { lead: string; old: string; now: string } {
  const before = codeLabel(preview.parentVersionCode);
  const after = codeLabel(preview.pushedVersionCode);
  return tense === "preview"
    ? {
        lead: `As ${community}'s page will show it, with Show changes from Base on:`,
        old: `what it has now (${before})`,
        now: `what it gets (${after})`,
      }
    : {
        lead: `As ${community}'s page showed it, with Show changes from Base on:`,
        old: `what it had (${before})`,
        now: `what it got (${after})`,
      };
}

/** "1 change", "3 changes": the push screens' count wording. */
export const changes = (n: number) => `${n} ${n === 1 ? "change" : "changes"}`;

/** The polite announcement once the preview is in (the brief). */
export function readyAnnouncement(community: string, counts: PushCounts): string {
  return `Preview for ${community} ready: ${changes(counts.total)}, ${counts.notApplied} not applied.`;
}

/** The type filter's choices: All always, then each kind with a change. */
export function filterOptions(
  counts: PushCounts,
): { key: PushFilter; label: string; count: number }[] {
  return [
    { key: "all", label: "All", count: counts.total },
    ...PUSH_KINDS.filter((kind) => counts[kind] > 0).map((kind) => ({
      key: kind,
      label: PUSH_KIND_LABEL[kind],
      count: counts[kind],
    })),
  ];
}

/** "{m} not applied ↓", the jump to the section. */
export const notAppliedJump = (count: number) => `${count} not applied ↓`;

/** The section's heading: "3 changes not applied". */
export const notAppliedHeading = (count: number) => `${changes(count)} not applied`;

/** A Draft tab's label: "Published version (26 · 2 not applied)". */
export function tabLabel(name: string, counts: PushCounts): string {
  return counts.notApplied > 0
    ? `${name} (${counts.total} · ${counts.notApplied} not applied)`
    : `${name} (${counts.total})`;
}

/**
 * The Draft tab's note, when the push moves the draft's code: "The draft's
 * code moves from v1.1 to v1.2, past the new published v1.1."
 */
export function draftCodeNote(
  move: { old: string; new: string } | null,
  publishedCode: string | null,
): string | null {
  if (move === null) return null;
  const past = publishedCode ? `, past the new published v${publishedCode}` : "";
  return `The draft's code moves from v${move.old} to v${move.new}${past}.`;
}

// ---------------------------------------------------------------------------
// The rows: the page's own shapes, and what the push does to each.
// ---------------------------------------------------------------------------

/** A drawn line's push facts. */
export interface LinePushFacts {
  /** The line's own kinds (R8: its own row is tinted and carries the icon). */
  kinds: readonly PushKind[];
  /** Every kind the type filter shows it under. */
  filterKinds: readonly PushKind[];
  /** A move's groups, by name (R7). */
  move: { from: string; to: string } | null;
}

/** What `SpecLineRows` takes in the preview: one line's push facts. */
export interface SpecLinePush {
  /** "Coastal V8": who the sentences name. */
  by: string;
  facts: LinePushFacts;
  /** The touched selections' kinds; only these draw under a line's own row (R4). */
  selections: ReadonlyMap<number, PushKind>;
}

/** A whole row the push adds or removes: its badge, and its one sentence (R5, R6). */
export function wholeRowKind(kinds: readonly PushKind[]): "added" | "removed" | null {
  if (kinds.includes("removed")) return "removed";
  if (kinds.includes("added")) return "added";
  return null;
}

/**
 * A row's badge (R5, R6). A wholly added or removed line badges its heading
 * and its selections go unbadged under it; otherwise a selection the push adds
 * or removes badges its own row.
 */
export function pushRowBadge(
  facts: LinePushFacts,
  selectionKind: PushKind | undefined,
  row: "child" | "single",
): "added" | "removed" | null {
  const lineKind = wholeRowKind(facts.kinds);
  const own = selectionKind === "added" || selectionKind === "removed" ? selectionKind : null;
  if (row === "single") return lineKind ?? own;
  return lineKind === null ? own : null;
}

/** One group card's worth: the group as `SpecGroup` takes it, and its lines. */
export interface PreviewGroup {
  group: SpecLinesGroup;
  kinds: readonly PushKind[];
  lines: SpecLinesLine[];
}

export interface PreviewModel {
  groups: PreviewGroup[];
  lines: ReadonlyMap<number, LinePushFacts>;
  selections: ReadonlyMap<number, PushKind>;
}

/** The answer's rows, grouped in the spec's group order as the server sent them. */
export function previewModel(preview: SpecPushPreview): PreviewModel {
  const groupKinds = new Map(preview.groupFacts.map((f) => [f.groupId, f.kinds]));
  const groupNames = new Map(preview.groups.map((g) => [g.id, g.name]));
  const lines = new Map<number, LinePushFacts>(
    preview.lineFacts.map((f) => {
      const line = preview.lines.find((l) => l.id === f.lineId);
      return [
        f.lineId,
        {
          kinds: f.kinds,
          filterKinds: f.filterKinds,
          move:
            f.fromGroup !== null && line !== undefined
              ? { from: f.fromGroup, to: groupNames.get(line.groupId) ?? "" }
              : null,
        },
      ];
    }),
  );
  return {
    groups: preview.groups.map((group) => ({
      group,
      kinds: groupKinds.get(group.id) ?? [],
      lines: preview.lines.filter((line) => line.groupId === group.id),
    })),
    lines,
    selections: new Map(preview.selectionFacts.map((f) => [f.selectionId, f.kind])),
  };
}

/**
 * The cards a filter shows: All shows every card; a kind shows each line
 * with a change of that kind, and a card whose own header has it alone.
 */
export function filteredGroups(model: PreviewModel, filter: PushFilter): PreviewGroup[] {
  if (filter === "all") return model.groups;
  return model.groups.flatMap((entry) => {
    const lines = entry.lines.filter((line) =>
      model.lines.get(line.id)?.filterKinds.includes(filter),
    );
    if (lines.length === 0 && !entry.kinds.includes(filter)) return [];
    return [{ ...entry, lines }];
  });
}

// --- the info icon's sentences (R2) ------------------------------------------------

const SPEC_TYPE_FOR_LABELS = "Base";

function changedBy(by: string, labels: readonly string[]): string | null {
  return labels.length === 0 ? null : `Changed by ${by}: ${labels.join(", ")}.`;
}

function lineLabels(line: SpecLinesLine): string[] {
  return line.changedFields
    .filter((field) => field !== "deleted")
    .map((field) => lineFieldLabel(field, SPEC_TYPE_FOR_LABELS));
}

function selectionLabels(selection: SpecLinesSelection | undefined): string[] {
  return (selection?.changedFields ?? [])
    .filter((field) => field !== "deleted")
    .map((field) => selectionFieldLabel(field, SPEC_TYPE_FOR_LABELS));
}

/** A whole row the push adds or removes is its one sentence. */
const wholeRow = (kind: PushKind | undefined, by: string) =>
  kind === "added" ? `Added by ${by}.` : kind === "removed" ? `Removed by ${by}.` : null;

/**
 * The sentence a row's info icon says, which is also its accessible name
 * (the brief, R2): a whole row added or removed says so; otherwise a move
 * names both groups, and the fields changed follow in #1043's words — the
 * line's then, on a one-selection row, its selection's. `null` when the row
 * itself is untouched (a line whose only change is under it, R8).
 */
export function pushRowSentence(
  by: string,
  facts: LinePushFacts,
  line: SpecLinesLine,
  selection: SpecLinesSelection | undefined,
  selectionKind: PushKind | undefined,
  row: "parent" | "single" | "child",
): string | null {
  if (row === "child") {
    // A child of a line the push adds or removes says the line's (#1049's).
    return (
      wholeRow(wholeRowKind(facts.kinds) ?? selectionKind, by) ??
      (selectionKind === "changed" ? changedBy(by, selectionLabels(selection)) : null)
    );
  }
  const whole = wholeRow(wholeRowKind(facts.kinds) ?? undefined, by);
  if (whole) return whole;
  if (row === "single") {
    const selectionWhole = wholeRow(selectionKind, by);
    if (selectionWhole) return selectionWhole;
  }
  const parts = [
    facts.move ? `Moved from ${facts.move.from} to ${facts.move.to} by ${by}.` : null,
    changedBy(by, [
      ...(facts.kinds.includes("changed") ? lineLabels(line) : []),
      ...(row === "single" && selectionKind === "changed" ? selectionLabels(selection) : []),
    ]),
  ].filter((part): part is string => part !== null);
  return parts.length === 0 ? null : parts.join(" ");
}

/** A group header's sentence: added, removed, or its changed name and notes. */
export function pushGroupSentence(
  by: string,
  kinds: readonly PushKind[],
  changedFields: readonly ("name" | "notes")[],
): string | null {
  if (kinds.includes("added")) return `Added by ${by}.`;
  if (kinds.includes("removed")) return `Removed by ${by}.`;
  if (!kinds.includes("changed")) return null;
  return changedBy(
    by,
    changedFields.map((field) => GROUP_FIELD_LABEL[field]),
  );
}

// ---------------------------------------------------------------------------
// The not-applied section (Q14, R9)
// ---------------------------------------------------------------------------

type NotAppliedEntry = SpecPushPreview["notApplied"][number];
type HeldItem = NonNullable<NotAppliedEntry["base"]["item"]>;

/** One side of a not-applied row: words, or an item with its picture. */
export type HeldShown = { text: string | null } | { item: HeldItem | null };

export interface NotAppliedRow {
  key: string;
  /** The Line column: the row's line, then "{group} · {what}". */
  line: string;
  where: string;
  base: HeldShown;
  keeps: HeldShown;
  why: string;
}

function whatOf(entry: NotAppliedEntry): string {
  const { field, entity } = entry;
  if (field === null) {
    if (entry.reason === "deleted-the-group") return "New line";
    if (entry.reason === "deleted-the-line") return "New selection";
    return entity === "selection" ? "Selection removed" : "Line removed";
  }
  if (field === "group") return "Group";
  if (field === "roomNotes") return `Room notes (${entry.roomName ?? ""})`;
  if (entity === "group" && (field === "name" || field === "notes")) {
    return GROUP_FIELD_LABEL[field];
  }
  if (entity === "room") return "Room name";
  if (entity === "selection") {
    return selectionFieldLabel(field as Parameters<typeof selectionFieldLabel>[0], "Base");
  }
  return lineFieldLabel(field as Parameters<typeof lineFieldLabel>[0], "Base");
}

const LOCATION_TYPES = new Set(["applicable", "per_plan", "specific"]);

function shownSide(
  entry: NotAppliedEntry,
  side: NotAppliedEntry["base"],
  which: "base" | "community",
): HeldShown {
  const { field } = entry;
  if (field === "item") return { item: side.item };
  if (field === null) {
    if (which === "base") {
      if (entry.reason === "deleted-the-line") return { item: side.item };
      if (entry.reason === "deleted-the-group") return { text: side.text };
      return { text: "Removed" };
    }
    if (entry.reason === "deleted-the-group")
      return { text: `${entry.groupName ?? ""} group removed` };
    if (entry.reason === "deleted-the-line") return { text: "Line removed" };
    return { text: "Kept" };
  }
  if (field === "perPlan") return { text: side.flag ? "Per plan" : NOT_PER_PLAN_TEXT };
  if (field === "paidUpgrade") return { text: side.flag ? "Paid upgrade" : NOT_PAID_UPGRADE_TEXT };
  if (field === "deleted") return { text: side.flag ? "Removed" : "Not removed" };
  if (field === "locationType" && side.text && LOCATION_TYPES.has(side.text)) {
    return {
      text:
        side.text === "specific"
          ? "Specific locations"
          : locationsText(side.text as LineLocationType, []),
    };
  }
  if (field === "notes" || field === "roomNotes") {
    const text = notesText(side.text);
    return { text: text === "" ? null : text };
  }
  return { text: side.text };
}

/** Why the push leaves a row alone (Q14's ruled words; the fourth, Assumption 7's). */
export function notAppliedReason(entry: NotAppliedEntry, community: string): string {
  switch (entry.reason) {
    case "changed-it":
      return `${community} changed this line, so it keeps its own choice.`;
    case "deleted-the-line":
      return `${community} removed this line, so it wasn't added back.`;
    case "deleted-the-group":
      return `${community} removed the ${entry.groupName ?? ""} group, so this change wasn't added back.`;
    case "room-name-taken":
      return `${community} already has a room named ${entry.base.text ?? ""}, so this rename wasn't applied.`;
  }
}

/** The section's table rows, in the list's order. */
export function notAppliedRows(preview: SpecPushPreview, community: string): NotAppliedRow[] {
  return preview.notApplied.map((entry, index) => {
    const what = whatOf(entry);
    return {
      key: `${index}`,
      line:
        entry.entity === "selection"
          ? (entry.lineName ?? "")
          : entry.entity === "room" || entry.entity === "group"
            ? (entry.name ?? "")
            : (entry.lineName ?? entry.name ?? ""),
      where: entry.groupName && entry.entity !== "group" ? `${entry.groupName} · ${what}` : what,
      base: shownSide(entry, entry.base, "base"),
      keeps: shownSide(entry, entry.community, "community"),
      why: notAppliedReason(entry, community),
    };
  });
}

/** Which list a preview opens on, and the Draft tabs when both lists exist (Assumption 16). */
export function previewTabs(preview: Pick<SpecPushPreview, "published" | "draft">): {
  tab: SpecPushPreviewTab;
  counts: PushCounts;
}[] {
  return [
    ...(preview.published ? [{ tab: "published" as const, counts: preview.published }] : []),
    ...(preview.draft ? [{ tab: "draft" as const, counts: preview.draft }] : []),
  ];
}
